import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Wrench, Settings, Lightbulb, Phone, X, Check, ArrowRight } from "lucide-react";

const ICON_MAP = { Wrench, Settings, Lightbulb, Phone };
const ICON_COLORS = [
    "bg-primary/10 text-primary",
    "bg-blue-500/10 text-blue-600",
    "bg-amber-500/10 text-amber-600",
    "bg-emerald-500/10 text-emerald-600",
];

export default function Services() {
    const [services, setServices] = React.useState([]);
    const [picked, setPicked] = React.useState(null);
    const [form, setForm] = React.useState({ name: "", phone: "", email: "", address: "", city: "", preferred_time: "", description: "" });
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        api.get("/services/catalog").then(r => setServices(r.data || [])).catch(() => {});
    }, []);

    const open = (svc) => {
        setPicked(svc);
        setForm({ name: "", phone: "", email: "", address: "", city: "", preferred_time: "", description: "" });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.phone) return toast.error("Nume și telefon obligatorii");
        setSubmitting(true);
        try {
            const res = await api.post("/service-requests", { ...form, service_type: picked.name });
            toast.success(`Cerere trimisă! Număr ticket: #${res.data.ticket_number}. Te contactăm în curând.`);
            setPicked(null);
        } catch (err) { toast.error(err.response?.data?.detail || "Eroare la trimitere"); }
        setSubmitting(false);
    };

    return (
        <div data-testid="services-page">
            {/* Hero */}
            <section className="bg-foreground text-background rounded-b-[2rem] overflow-hidden">
                <div className="container-x py-14 md:py-20">
                    <div className="overline text-primary mb-3">Profesionalism garantat</div>
                    <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight max-w-2xl">
                        Serviciile noastre
                    </h1>
                    <p className="text-background/70 mt-4 max-w-xl text-base sm:text-lg">
                        Un click — și te sunăm noi. Echipa noastră te consiliază gratuit și îți oferă o programare la cel mai potrivit moment.
                    </p>
                </div>
            </section>

            <div className="container-x py-12 sm:py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {services.map((s, i) => {
                        const Icon = ICON_MAP[s.icon] || Wrench;
                        const colorClass = ICON_COLORS[i % ICON_COLORS.length];
                        return (
                            <div key={s.service_id || i}
                                className="bg-card rounded-2xl border border-border/60 hover:border-primary/30 hover:shadow-lg transition-all duration-300 p-6 flex flex-col animate-fade-up"
                                style={{ animationDelay: `${i * 0.07}s` }}
                                data-testid={`service-${i}`}>
                                <div className={`w-14 h-14 rounded-2xl ${colorClass} flex items-center justify-center mb-5 flex-shrink-0`}>
                                    <Icon className="h-7 w-7" />
                                </div>
                                <h3 className="font-heading text-xl font-semibold mb-2">{s.name}</h3>
                                <p className="text-muted-foreground text-sm mb-4 leading-relaxed">{s.description}</p>
                                <ul className="space-y-2 text-sm mb-6 flex-1">
                                    {(s.features || []).map((f, j) => (
                                        <li key={j} className="flex items-start gap-2.5">
                                            <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <Check className="h-2.5 w-2.5 text-primary" />
                                            </div>
                                            <span className="text-muted-foreground">{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={() => open(s)}
                                    className="btn-primary w-full mt-auto"
                                    data-testid={`service-request-${i}`}>
                                    Solicită acest serviciu
                                </button>
                            </div>
                        );
                    })}
                </div>

                {services.length === 0 && (
                    <div className="py-20 text-center text-muted-foreground">
                        <div className="text-4xl mb-4">🔧</div>
                        <p>Serviciile vor fi disponibile în curând.</p>
                    </div>
                )}

                {/* CTA */}
                <div className="mt-14 bg-foreground text-background rounded-2xl p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <div className="overline text-primary mb-2">Nu ești sigur ce îți trebuie?</div>
                        <div className="font-heading text-2xl sm:text-3xl mb-2">Hai să discutăm</div>
                        <p className="text-background/70 text-sm">Consultanță gratuită, fără obligații.</p>
                    </div>
                    <Link to="/contact" className="flex-shrink-0 flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl font-semibold hover:brightness-110 transition-all whitespace-nowrap">
                        Contactează-ne <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>

            {/* Service request modal */}
            {picked && (
                <div className="fixed inset-0 z-[200] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setPicked(null)}>
                    <div className="bg-card rounded-t-2xl sm:rounded-2xl max-w-xl w-full max-h-[88vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Solicitare serviciu</div>
                                <h3 className="font-heading text-xl">{picked.name}</h3>
                            </div>
                            <button onClick={() => setPicked(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <input required placeholder="Nume complet *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="input-soft" data-testid="svc-name" />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input required placeholder="Telefon *" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="input-soft" data-testid="svc-phone" />
                                <input type="email" placeholder="Email (opțional)" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="input-soft" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <input placeholder="Adresă" value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="input-soft" />
                                <input placeholder="Oraș" value={form.city} onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))} className="input-soft" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground block mb-1">Interval orar preferat</label>
                                <input placeholder="ex: 10:00-14:00" value={form.preferred_time} onChange={(e) => setForm(f => ({ ...f, preferred_time: e.target.value }))} className="input-soft" data-testid="svc-time" />
                            </div>
                            <textarea rows="4" placeholder="Descriere / detalii (opțional)" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} className="input-soft" />
                            <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="svc-submit">
                                {submitting ? "Se trimite..." : "Trimite solicitarea"}
                            </button>
                            <p className="text-xs text-muted-foreground text-center">Te contactăm în maxim 24h pentru confirmare.</p>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
